import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
	api,
	ApiError,
	type ProposeEditResult,
	type ToolAgentState,
	type ToolEntry,
} from "../lib/api.ts";

/**
 * Single-tool profile — name, description, permission rule, per-agent matrix.
 *
 * The matrix lets the owner pick `inherit | allow | deny` per agent. State is
 * persisted into the existing `agent-claude-settings.json::permissions` arrays
 * via `api.setAgentClaudeSettings()` (one PR per change, auto-merged). No new
 * backend route — the route just exposes the catalog; mutations re-use the
 * agent-level claude-settings endpoint.
 *
 * Orchestrator tools hide the matrix (not reachable from project-agents).
 * Plugin / mcp-stdio "skill-level" entries hide the matrix too (no
 * permission rule to attach).
 */
export function ToolProfile() {
	const { fullKey } = useParams<{ fullKey: string }>();
	const [tool, setTool] = useState<ToolEntry | null>(null);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(() => {
		if (!fullKey) return;
		api
			.getTool(fullKey)
			.then((res) => setTool(res.tool))
			.catch((err) => {
				if (err instanceof ApiError && err.status === 404) {
					setError(`Tool "${fullKey}" not found.`);
				} else {
					setError((err as Error).message);
				}
			});
	}, [fullKey]);

	useEffect(refresh, [refresh]);

	if (error) {
		return (
			<div className="px-6 py-8">
				<Link to="/tools" className="text-sm text-zinc-500 hover:text-zinc-300">
					← back to tools
				</Link>
				<div className="mt-4 p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300">
					{error}
				</div>
			</div>
		);
	}

	if (!tool) {
		return <div className="px-6 py-8 text-zinc-500">Loading…</div>;
	}

	const matrixVisible = tool.permissionRule !== null && tool.agents.length > 0;

	return (
		<div className="px-6 py-8 max-w-4xl mx-auto">
			<Link to="/tools" className="text-sm text-zinc-500 hover:text-zinc-300">
				← back to tools
			</Link>

			<div className="mt-4 mb-6">
				<div className="flex items-baseline gap-3">
					<h1 className="text-xl font-semibold font-mono">{tool.name}</h1>
					<SourceBadge source={tool.source} />
				</div>
				{tool.source.kind === "mcp" && (
					<div className="text-sm text-zinc-500 mt-2">
						Exposed by{" "}
						<Link
							to={`/skills/${encodeURIComponent(tool.source.skillKey)}`}
							className="text-zinc-300 hover:text-zinc-100 underline-offset-2 hover:underline"
						>
							{tool.source.serverDisplayName}
						</Link>{" "}
						<span className="text-zinc-600 font-mono">({tool.source.skillKey})</span>
					</div>
				)}
				{tool.source.kind === "plugin" && (
					<div className="text-sm text-zinc-500 mt-2">
						Skill-level entry —{" "}
						<Link
							to={`/skills/${encodeURIComponent(tool.source.skillKey)}`}
							className="text-zinc-300 hover:text-zinc-100 underline-offset-2 hover:underline"
						>
							see skill
						</Link>{" "}
						for details. Per-tool gating not available.
					</div>
				)}
			</div>

			<section className="mb-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
				<header className="px-4 py-3 border-b border-zinc-800">
					<h2 className="text-sm font-semibold text-zinc-100">Description</h2>
				</header>
				<div className="p-4 text-sm text-zinc-300">
					{tool.description ?? <span className="text-zinc-500 italic">No description.</span>}
				</div>
			</section>

			{tool.permissionRule !== null && (
				<section className="mb-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
					<header className="px-4 py-3 border-b border-zinc-800">
						<h2 className="text-sm font-semibold text-zinc-100">Permission rule</h2>
						<div className="text-[11px] text-zinc-500 mt-0.5">
							Used in <code className="font-mono">permissions.allow</code> /{" "}
							<code className="font-mono">permissions.deny</code> in each agent's claude
							settings.
						</div>
					</header>
					<div className="p-4">
						<code className="text-xs font-mono text-zinc-200 bg-zinc-950/60 border border-zinc-800 rounded px-2 py-1">
							{tool.permissionRule}
						</code>
					</div>
				</section>
			)}

			{matrixVisible ? (
				<AgentMatrix tool={tool} onChange={refresh} />
			) : tool.source.kind === "orchestrator" ? (
				<div className="text-xs text-zinc-500 italic">
					Orchestrator tools run inside the manager-agent itself (owner chat at
					agent.g1.group). Project-agents don't have access — nothing to gate.
				</div>
			) : null}
		</div>
	);
}

function AgentMatrix({ tool, onChange }: { tool: ToolEntry; onChange: () => void }) {
	return (
		<section className="mb-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800">
				<h2 className="text-sm font-semibold text-zinc-100">Agent access</h2>
				<div className="text-[11px] text-zinc-500 mt-0.5">
					<strong>inherit</strong> = Claude Code's default-ask. <strong>allow</strong> = pre-approved.{" "}
					<strong>deny</strong> = blocked even if the skill is assigned. Each change opens a PR;
					agent picks it up after restart.
				</div>
			</header>
			<div className="divide-y divide-zinc-800">
				{tool.agents.map((a) => (
					<AgentRow
						key={a.key}
						tool={tool}
						agent={a}
						permissionRule={tool.permissionRule!}
						onChange={onChange}
					/>
				))}
			</div>
		</section>
	);
}

type Mode = "inherit" | "allow" | "deny";

function modeOf(state: ToolAgentState): Mode {
	if (state.denied) return "deny";
	if (state.allowed) return "allow";
	return "inherit";
}

function AgentRow({
	agent,
	permissionRule,
	onChange,
}: {
	tool: ToolEntry;
	agent: ToolAgentState;
	permissionRule: string;
	onChange: () => void;
}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pr, setPr] = useState<ProposeEditResult | null>(null);
	const mode = modeOf(agent);

	async function applyMode(next: Mode) {
		if (next === mode) return;
		setBusy(true);
		setError(null);
		setPr(null);
		try {
			const current = await api.getAgent(agent.key);
			const settings: Record<string, unknown> = current.claudeSettings
				? structuredClone(current.claudeSettings)
				: {};
			const perms = (settings.permissions as Record<string, unknown> | undefined) ?? {};
			const allow = new Set(Array.isArray(perms.allow) ? (perms.allow as string[]) : []);
			const deny = new Set(Array.isArray(perms.deny) ? (perms.deny as string[]) : []);

			allow.delete(permissionRule);
			deny.delete(permissionRule);
			if (next === "allow") allow.add(permissionRule);
			else if (next === "deny") deny.add(permissionRule);

			const newPerms: Record<string, unknown> = { ...perms };
			if (allow.size > 0) newPerms.allow = [...allow];
			else delete newPerms.allow;
			if (deny.size > 0) newPerms.deny = [...deny];
			else delete newPerms.deny;

			const newSettings: Record<string, unknown> = { ...settings };
			if (Object.keys(newPerms).length > 0) newSettings.permissions = newPerms;
			else delete newSettings.permissions;

			const payload =
				Object.keys(newSettings).length > 0 ? newSettings : null;
			const result = await api.setAgentClaudeSettings(agent.key, payload);
			if (!("noop" in result)) setPr(result);
			onChange();
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	const skillNotAssigned = !agent.granted;
	const inheritDisabled = busy || skillNotAssigned;

	return (
		<div className="px-4 py-3 flex flex-col gap-2">
			<div className="flex items-baseline justify-between gap-3">
				<div className="min-w-0">
					<Link
						to={`/agents/${encodeURIComponent(agent.key)}`}
						className="text-sm text-zinc-100 hover:text-white underline-offset-2 hover:underline"
					>
						{agent.displayName}
					</Link>
					<span className="ml-2 text-[11px] text-zinc-500 font-mono">{agent.key}</span>
					{skillNotAssigned && (
						<span
							className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700"
							title="Underlying skill is not assigned to this agent — allow/deny rules won't take effect until you assign it."
						>
							skill not assigned
						</span>
					)}
				</div>
				<div className="flex gap-1 shrink-0">
					{(["inherit", "allow", "deny"] as Mode[]).map((m) => {
						const active = mode === m;
						const cls = active
							? m === "allow"
								? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200"
								: m === "deny"
									? "bg-red-500/20 border-red-500/60 text-red-200"
									: "bg-zinc-700/40 border-zinc-600 text-zinc-100"
							: "border-zinc-700 hover:border-zinc-500 text-zinc-400";
						return (
							<button
								key={m}
								type="button"
								onClick={() => applyMode(m)}
								disabled={inheritDisabled && m !== mode}
								className={`text-[11px] px-2 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed ${cls}`}
							>
								{m}
							</button>
						);
					})}
				</div>
			</div>
			{busy && <div className="text-[11px] text-zinc-500">Opening PR…</div>}
			{error && <div className="text-[11px] text-red-300">{error}</div>}
			{pr && (
				<div className="text-[11px] text-emerald-300">
					{pr.merged
						? "✓ Auto-merged — restart agent to apply."
						: "✓ PR opened — auto-merge didn't take, finish it manually."}{" "}
					<a href={pr.htmlUrl} target="_blank" rel="noreferrer" className="underline break-all">
						{pr.htmlUrl}
					</a>
				</div>
			)}
		</div>
	);
}

function SourceBadge({ source }: { source: ToolEntry["source"] }) {
	const map: Record<ToolEntry["source"]["kind"], { label: string; cls: string }> = {
		mcp: { label: "MCP", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
		builtin: { label: "Built-in", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
		orchestrator: {
			label: "Orchestrator",
			cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
		},
		plugin: { label: "Plugin", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
	};
	const { label, cls } = map[source.kind];
	return (
		<span className={`text-[10px] px-2 py-0.5 rounded border ${cls} whitespace-nowrap`}>
			{label}
		</span>
	);
}
